/**
 * Merchant store hooks using React Relay.
 *
 * Merchant stores are the markers a partner's branded rider app shows on its
 * map, scoped to the organization that owns the branded app.
 *
 * Note: Run `npm run relay` after making changes to GraphQL queries
 * to regenerate the type artifacts.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { fetchQuery } from 'react-relay';
import graphql from 'babel-plugin-relay/macro';
import { relayEnvironment } from '../../../../relay';
import type {
  useMerchantStoresRelayQuery as UseMerchantStoresRelayQuery,
  useMerchantStoresRelayQuery$variables,
  MerchantStoreFilterInput,
  MerchantStoreSortInput,
  MerchantStoreState,
} from './__generated__/useMerchantStoresRelayQuery.graphql';
import type { useMerchantStoresRelayByIdQuery } from './__generated__/useMerchantStoresRelayByIdQuery.graphql';

// ============================================================================
// GraphQL Definitions
// ============================================================================

const MerchantStoresListQuery = graphql`
  query useMerchantStoresRelayQuery(
    $first: Int
    $after: String
    $filter: MerchantStoreFilterInput
    $sort: [MerchantStoreSortInput!]
  ) {
    listMerchantStores(first: $first, after: $after, filter: $filter, sort: $sort) {
      count
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      edges {
        cursor
        node {
          id
          oldId
          name
          websiteUrl
          lat
          lng
          primaryColor
          secondaryColor
          organizationId
          state
          logo {
            id
            url
          }
          banner {
            id
            url
          }
        }
      }
    }
  }
`;

const MerchantStoreByIdQuery = graphql`
  query useMerchantStoresRelayByIdQuery($id: ID!) {
    getMerchantStore(id: $id) {
      id
      oldId
      name
      websiteUrl
      lat
      lng
      primaryColor
      secondaryColor
      organizationId
      state
      logo {
        id
        url
      }
      banner {
        id
        url
      }
    }
  }
`;

// ============================================================================
// Types
// ============================================================================

export interface MerchantStoreImageGraphQL {
  id: string;
  url: string | null;
}

export interface MerchantStoreGraphQL {
  id: string;
  oldId: string | null;
  name: string;
  websiteUrl: string | null;
  lat: number | null;
  lng: number | null;
  primaryColor: string | null;
  secondaryColor: string | null;
  organizationId: string;
  state: MerchantStoreState | null;
  logo: MerchantStoreImageGraphQL | null;
  banner: MerchantStoreImageGraphQL | null;
}

export interface MerchantStoreQueryFilter {
  organizationId?: string;
  state?: MerchantStoreState;
  name?: string;
}

export type { MerchantStoreFilterInput, MerchantStoreSortInput, MerchantStoreState };

/**
 * Translates the app-level filter into the Ash filter input.
 *
 * Exported so the name-search translation can be unit-tested without a Relay
 * environment, the way `useRatesRelay`'s is.
 */
export function buildRelayFilter(
  filter?: MerchantStoreQueryFilter
): MerchantStoreFilterInput | undefined {
  if (!filter) return undefined;

  const relayFilter: MerchantStoreFilterInput = {};

  if (filter.organizationId) {
    relayFilter.organizationId = { eq: filter.organizationId };
  }
  if (filter.state) {
    relayFilter.state = { eq: filter.state };
  }
  if (filter.name) {
    // A caller that supplies its own wildcards means them literally.
    relayFilter.name = filter.name.includes('%')
      ? { ilike: filter.name }
      : { ilike: `%${filter.name}%` };
  }

  return Object.keys(relayFilter).length > 0 ? relayFilter : undefined;
}

// ============================================================================
// Hooks
// ============================================================================

/**
 * Keyset-paginated merchant stores for an organization.
 *
 * Does NOT use Suspense — returns the react-query-like surface the tables in
 * this app expect, including the `endCursor`/`hasMore` pair that
 * `useKeysetPagination` drives.
 */
export function useMerchantStoresRelayQuery(
  filter?: MerchantStoreQueryFilter,
  options?: {
    limit?: number;
    after?: string | null;
    sort?: MerchantStoreSortInput[];
    enabled?: boolean;
  }
) {
  const [data, setData] = useState<MerchantStoreGraphQL[] | null>(null);
  const [count, setCount] = useState<number | undefined>(undefined);
  const [hasMore, setHasMore] = useState(false);
  const [endCursor, setEndCursor] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isFetched, setIsFetched] = useState(false);
  const [isRefetching, setIsRefetching] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [fetchCount, setFetchCount] = useState(0); // For manual refetch trigger

  const limit = options?.limit ?? 100;
  const after = options?.after ?? undefined;
  const sort = useMemo(
    () => options?.sort ?? [{ field: 'NAME' as const, order: 'ASC' as const }],
    [options?.sort]
  );
  const enabled = options?.enabled !== false;

  const filterKey = useMemo(() => JSON.stringify(filter), [filter]);

  useEffect(() => {
    if (!enabled) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    const doFetch = async () => {
      try {
        if (isFetched) {
          setIsRefetching(true);
        } else {
          setIsLoading(true);
        }

        const variables: useMerchantStoresRelayQuery$variables = {
          first: limit,
          after,
          filter: buildRelayFilter(filter),
          sort,
        };

        const result = await fetchQuery<UseMerchantStoresRelayQuery>(
          relayEnvironment,
          MerchantStoresListQuery,
          variables
        ).toPromise();

        if (cancelled) return;

        if (result) {
          const nodes =
            result.listMerchantStores?.edges
              ?.map((edge: any) => edge?.node)
              .filter(Boolean) ?? [];
          setData(nodes as MerchantStoreGraphQL[]);
          setCount(result.listMerchantStores?.count ?? undefined);
          setHasMore(result.listMerchantStores?.pageInfo?.hasNextPage ?? false);
          setEndCursor(result.listMerchantStores?.pageInfo?.endCursor ?? null);
          setError(null);
        }
      } catch (err) {
        if (cancelled) return;
        setError(
          err instanceof Error ? err : new Error('Failed to fetch merchant stores')
        );
      } finally {
        if (!cancelled) {
          setIsLoading(false);
          setIsFetched(true);
          setIsRefetching(false);
        }
      }
    };

    doFetch();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, filterKey, limit, after, sort, fetchCount]);

  const refetch = useCallback(() => {
    setFetchCount((c) => c + 1);
  }, []);

  return {
    data,
    count,
    hasMore,
    endCursor,
    isLoading,
    isFetched,
    isRefetching,
    error,
    refetch,
  };
}

/** A single merchant store by its Ash id, for the detail and edit pages. */
export function useMerchantStoreByIdRelayQuery(
  id?: string | null,
  options?: { enabled?: boolean }
) {
  const [data, setData] = useState<MerchantStoreGraphQL | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isFetched, setIsFetched] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [fetchCount, setFetchCount] = useState(0);

  const enabled = options?.enabled !== false && Boolean(id);

  useEffect(() => {
    if (!enabled || !id) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    const doFetch = async () => {
      try {
        setIsLoading(true);

        const result = await fetchQuery<useMerchantStoresRelayByIdQuery>(
          relayEnvironment,
          MerchantStoreByIdQuery,
          { id }
        ).toPromise();

        if (cancelled) return;

        if (result) {
          setData((result.getMerchantStore as MerchantStoreGraphQL) ?? null);
          setError(null);
        }
      } catch (err) {
        if (cancelled) return;
        setError(
          err instanceof Error ? err : new Error('Failed to fetch merchant store')
        );
      } finally {
        if (!cancelled) {
          setIsLoading(false);
          setIsFetched(true);
        }
      }
    };

    doFetch();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, id, fetchCount]);

  const refetch = useCallback(() => {
    setFetchCount((c) => c + 1);
  }, []);

  return { data, isLoading, isFetched, error, refetch };
}
